import LoadingModal from "../components/modals/LoadingModal";
import NotificationSnackbar from "../components/NotificationSnackbar";
import ImageModal from "../components/modals/ImageModal";
import { useState, useEffect } from "react";
import { Box, Container, Divider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { fetchAllActiveAssessments } from "../helpers/apiRequests";
import {
  CGI_GRADIENT_A,
  CGI_PURPLE,
  CGI_RED,
  languageKey,
} from "../helpers/enums";

export default function Catalogue({ language }) {
  const { t } = useTranslation();

  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [openLoading, setOpenLoading] = useState(false);

  const [allActiveAssessments, setAllActiveAssessments] = useState([]);

  async function fetchAllAssessments() {
    setOpenLoading(true);
    try {
      const fetchedAssessments = await fetchAllActiveAssessments();
      const allAssessments = fetchedAssessments.map((assessment) => ({
        name: assessment.name,
        description: assessment.description,
        contactPerson: assessment.contactPerson,
        imageKey: assessment.imageKey,
        language: assessment.language,
      }));
      setAllActiveAssessments(allAssessments);
    } catch (e) {
      console.error(e);
      setNotificationMessage(t("errorFetchDataMessage"));
      setOpenNotification(true);
    }
    setOpenLoading(false);
  }

  useEffect(() => {
    fetchAllAssessments();
  }, []);

  return (
    <>
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", flexDirection: "column", mt: 5 }}>
          <Typography
            sx={{
              fontSize: 24,
              userSelect: "none",
              fontWeight: 500,
              color: CGI_RED,
            }}
          >
            {t("listOfAllAvailableAssessments")}
          </Typography>
          <Divider
            sx={{
              mt: 1,
              mb: 5,
              color: CGI_RED,
              borderBottom: 2,
            }}
          />
          {allActiveAssessments.map((assessment, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 12,
                border: "double 1px transparent",
                backgroundImage:
                  "linear-gradient(white, white)," + CGI_GRADIENT_A,
                backgroundOrigin: "border-box",
                backgroundClip: "content-box, border-box",
                mt: index === 0 ? 0 : 4,
                ml: index % 2 === 0 ? 7 : 0,
                mr: index % 2 === 1 ? 7 : 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  transform: "scale(0.975)",
                  borderRadius: 10,
                  border: "double 2px transparent",
                  backgroundImage:
                    "linear-gradient(white, white)," + CGI_GRADIENT_A,
                  backgroundOrigin: "border-box",
                  backgroundClip: "content-box, border-box",
                  my: 1.5,
                  width: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    py: 2,
                    px: 3,
                    width: 1,
                  }}
                  key={index}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <Typography
                        sx={{
                          background:
                            "-webkit-linear-gradient(45deg, #E31937, #A82465 60%, #5236AB)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          fontWeight: 500,
                          fontStyle: "italic",
                          fontSize: 19.5,
                          pr: 2,
                          userSelect: "none",
                        }}
                      >
                        {assessment.name}
                      </Typography>
                    </Box>
                    {assessment?.contactPerson && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            pr: 1,
                            fontStyle: "italic",
                            userSelect: "none",
                            color: CGI_PURPLE,
                          }}
                        >
                          {t("contactPerson")}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 500,
                            userSelect: "none",
                            color: CGI_PURPLE,
                          }}
                        >
                          {assessment.contactPerson}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Divider
                    sx={{
                      my: 1,
                      borderImageSlice: 1,
                      borderImageSource: CGI_GRADIENT_A,
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        align="justify"
                        sx={{
                          mr: 3,
                          userSelect: "none",
                          fontFamily: "Inter",
                          fontSize: 13.5,
                        }}
                      >
                        {assessment.description}
                      </Typography>
                      <Typography
                        align="justify"
                        sx={{
                          mr: 3,
                          ml: 1,
                          fontStyle: "italic",
                          mt: 1,
                          fontSize: 13.5,
                          userSelect: "none",
                          color: CGI_PURPLE,
                        }}
                      >
                        {`${t("language")}: `}
                        {t(languageKey[assessment.language], { lng: language })}
                      </Typography>
                    </Box>
                    <ImageModal
                      imageKey={assessment?.imageKey}
                      boxWidth={230}
                      boxHeight={230}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
      <LoadingModal open={openLoading} />
      <NotificationSnackbar
        message={notificationMessage}
        open={openNotification}
        setOpen={setOpenNotification}
      />
    </>
  );
}
